package com.arena_web.ui.components.cards

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div

/**
 * Horizontal pointer-drag wrapper for the active card in a deck-style
 * carousel. Used by:
 *  • Profile sidebar (`OverviewSidebar.CardCarousel`)
 *  • People detail popup (`PersonDetailDialog.CardGallery`)
 *
 * Behaviour:
 *  • While the user drags, [content] follows the pointer with a slight
 *    rotation + scale for live feedback. [onDragProgress] fires
 *    `(progress 0..1 of [threshold], dragging)` on every move so the host
 *    can animate the surrounding peek stack toward a "promoted" position.
 *  • On release past [threshold] in either direction:
 *    – peek stack is held at fully promoted (`onDragProgress(1.0, false)`),
 *    – the card runs a 4-stage horizontal-loop "fly to the back" animation
 *      (~760ms), passing BEHIND the peeks via a `z-index: -5` drop on the
 *      second half,
 *    – then [onNext]/[onPrev] fires (host shifts `currentIndex`), peek
 *      transitions are frozen for one frame, and the active card snaps
 *      back to centre with its new content.
 *  • On release below [threshold] the card springs back to centre.
 *
 * Pointer event handlers are attached once via `ref { }`, so we use
 * `rememberUpdatedState` for [onNext]/[onPrev]/[onDragProgress] to keep
 * the listeners pointing at the latest closures — otherwise repeated
 * swipes would keep calling the first lambda (stale `safeIndex`).
 */
@Composable
internal fun SwipeableDeckCard(
    enabled: Boolean,
    onNext: () -> Unit,
    onPrev: () -> Unit,
    onDragProgress: (progress: Double, dragging: Boolean) -> Unit = { _, _ -> },
    threshold: Double = 50.0,
    baseScale: Double = 1.0,
    content: @Composable () -> Unit,
) {
    if (!enabled) {
        content()
        return
    }
    val onNextRef = rememberUpdatedState(onNext)
    val onPrevRef = rememberUpdatedState(onPrev)
    val onDragProgressRef = rememberUpdatedState(onDragProgress)

    var dragX by remember { mutableStateOf(0.0) }
    var dragging by remember { mutableStateOf(false) }
    // -1 = flying off to the left (next), +1 = right (prev), 0 = idle.
    var flying by remember { mutableStateOf(0) }
    // 0 = idle, 1..4 = horizontal-loop stages.
    var flyPhase by remember { mutableStateOf(0) }
    var skipTransition by remember { mutableStateOf(false) }
    val startX = remember { mutableStateOf(0.0) }
    val activePointer = remember { mutableStateOf<Int?>(null) }
    val scope = rememberCoroutineScope()

    val isFlying = flying != 0

    val clampedX = dragX.coerceIn(-180.0, 180.0)
    val dragRotate = clampedX * 0.05
    val dragScale = baseScale - (kotlin.math.abs(clampedX) / 2400.0).coerceAtMost(0.04)

    // Horizontal loop: 1) sweep out, 2) peak far side, 3) cross back behind
    // (z-index drops), 4) settle into the back-of-deck slot. `ty` stays 0.
    val tx = when {
        !isFlying -> clampedX
        flyPhase == 1 -> flying.toDouble() * 220.0
        flyPhase == 2 -> flying.toDouble() * 320.0
        flyPhase == 3 -> flying.toDouble() * 180.0
        else -> 28.0
    }
    val rot = when {
        !isFlying -> dragRotate
        flyPhase == 1 -> flying.toDouble() * 12.0
        flyPhase == 2 -> flying.toDouble() * 32.0
        flyPhase == 3 -> flying.toDouble() * 22.0
        else -> 0.0
    }
    val sc = when {
        !isFlying -> dragScale
        flyPhase == 1 -> baseScale * 0.94
        flyPhase == 2 -> baseScale * 0.84
        flyPhase == 3 -> baseScale * 0.68
        else -> baseScale * 0.55
    }
    val op = when {
        !isFlying -> 1.0
        flyPhase == 1 -> 1.0
        flyPhase == 2 -> 0.85
        flyPhase == 3 -> 0.4
        else -> 0.0
    }
    val zIndex = when {
        !isFlying -> "auto"
        flyPhase >= 3 -> "-5"
        else -> "2"
    }

    Div(attrs = {
        style {
            property("display", "inline-block")
            property("transform", "translate(${tx}px, 0px) rotate(${rot}deg) scale($sc)")
            property("transform-origin", "center 80%")
            property("opacity", "$op")
            property("z-index", zIndex)
            property(
                "transition",
                when {
                    dragging || skipTransition -> "none"
                    isFlying -> "transform 0.19s ease-in-out, opacity 0.19s ease-in-out"
                    else -> "transform 0.36s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.2s"
                }
            )
            property("touch-action", "pan-y")
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("cursor", if (dragging) "grabbing" else "grab")
            property("will-change", "transform, opacity")
        }
        ref { el ->
            fun notifyProgress() {
                val p = (kotlin.math.abs(dragX) / threshold).coerceIn(0.0, 1.0)
                onDragProgressRef.value(p, dragging)
            }
            val onDown: (org.w3c.dom.events.Event) -> Unit = { ev ->
                if (!isFlying) {
                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                    dragging = true
                    startX.value = pe.clientX.toDouble()
                    dragX = 0.0
                    activePointer.value = pe.pointerId
                    runCatching { el.setPointerCapture(pe.pointerId) }
                    notifyProgress()
                }
            }
            val onMove: (org.w3c.dom.events.Event) -> Unit = { ev ->
                if (dragging) {
                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                    if (activePointer.value == null || activePointer.value == pe.pointerId) {
                        dragX = pe.clientX.toDouble() - startX.value
                        notifyProgress()
                    }
                }
            }
            val onUp: (org.w3c.dom.events.Event) -> Unit = { ev ->
                if (dragging) {
                    val pe = ev.unsafeCast<org.w3c.dom.pointerevents.PointerEvent>()
                    dragging = false
                    activePointer.value = null
                    runCatching { el.releasePointerCapture(pe.pointerId) }
                    when {
                        dragX <= -threshold -> {
                            flying = -1
                            flyPhase = 1
                            onDragProgressRef.value(1.0, false)
                            scope.launch {
                                delay(190); flyPhase = 2
                                delay(190); flyPhase = 3
                                delay(190); flyPhase = 4
                                delay(190)
                                onDragProgressRef.value(0.0, true)
                                onNextRef.value.invoke()
                                skipTransition = true
                                flying = 0
                                flyPhase = 0
                                dragX = 0.0
                                delay(20)
                                skipTransition = false
                                onDragProgressRef.value(0.0, false)
                            }
                        }
                        dragX >= threshold -> {
                            flying = 1
                            flyPhase = 1
                            onDragProgressRef.value(1.0, false)
                            scope.launch {
                                delay(190); flyPhase = 2
                                delay(190); flyPhase = 3
                                delay(190); flyPhase = 4
                                delay(190)
                                onDragProgressRef.value(0.0, true)
                                onPrevRef.value.invoke()
                                skipTransition = true
                                flying = 0
                                flyPhase = 0
                                dragX = 0.0
                                delay(20)
                                skipTransition = false
                                onDragProgressRef.value(0.0, false)
                            }
                        }
                        else -> {
                            dragX = 0.0
                            onDragProgressRef.value(0.0, false)
                        }
                    }
                }
            }
            el.addEventListener("pointerdown", onDown)
            el.addEventListener("pointermove", onMove)
            el.addEventListener("pointerup", onUp)
            el.addEventListener("pointercancel", onUp)
            el.addEventListener("pointerleave", onUp)
            onDispose {
                el.removeEventListener("pointerdown", onDown)
                el.removeEventListener("pointermove", onMove)
                el.removeEventListener("pointerup", onUp)
                el.removeEventListener("pointercancel", onUp)
                el.removeEventListener("pointerleave", onUp)
            }
        }
    }) {
        content()
    }
}

/**
 * Geometry for a single peek slot interpolating between its resting "base"
 * position in the deck and the position one step closer to the active slot
 * ("promoted"). Drives the deck-promotion animation that runs alongside a
 * user's swipe. Returns the final transform values to apply to the peek
 * `Box` for the given depth (1 = closest peek behind active, 2 = further).
 *
 * @param baseScale the scale of the active card (typically 1.0 for full
 *   PlayerCard layouts, lower for People popup where cards render at 90%).
 * @param progress 0..1 from the active card's swipe (cubed by the caller
 *   for an ease-in curve so early drag is gentle).
 */
internal data class PeekTransform(
    val tx: Double,
    val ty: Double,
    val scale: Double,
    val opacity: Double,
)

/**
 * Peek geometry tuned to Figma 7965:188716 (profile player-card deck).
 * The reference design positions:
 *  • active — scale 1.0, dx 0, dy 0
 *  • peek 1 — scale 0.90, dx +9,  dy +6
 *  • peek 2 — scale 0.81, dx +26, dy +9
 * (measured in the canvas where the active card image is 247×369).
 * The progression is non-linear: peek 1 hugs the active slot, peek 2
 * pushes much further out (and down) so the stack reads as a fanned deck
 * rather than evenly stepped slices.
 *
 * All offsets are in the OUTER container's CSS px space — for wider host
 * cards (e.g. People popup PersonCard at ~357 px), pass `widthScale` so
 * the dx/dy values stretch proportionally and the deck still reads the
 * same. `baseScale` here represents the active card's natural scale (1.0
 * for raw PlayerCard.Large, 0.9 for PersonCard which already self-scales).
 */
internal fun computePeekTransform(
    depth: Int,
    progress: Double,
    baseScale: Double = 1.0,
    widthScale: Double = 1.0,
    // Constant px added to every peek's x offset. Use when the active card
    // is larger than the formula's reference width (247 px) and the
    // Figma-tuned `initialTx` of 9·widthScale no longer clears the active
    // card's right edge. e.g. PersonDetailDialog's 1.4× cards need ~10 px
    // extra so peek 1 actually pokes out instead of hiding behind active.
    extraTx: Double = 0.0,
    // How much peek 1 shrinks relative to the active card (peek 1 scale =
    // baseScale - initialShrink) and how much each subsequent peek shrinks
    // beyond that. Figma reference: 0.10 then 0.09.
    initialShrink: Double = 0.10,
    scaleStep: Double = 0.09,
): PeekTransform {
    val initialTx = 9.0 * widthScale + extraTx
    val initialTy = 6.0 * widthScale
    val txStep = 17.0 * widthScale
    val tyStep = 3.0 * widthScale

    val baseTx = initialTx + (depth - 1) * txStep
    val baseTy = initialTy + (depth - 1) * tyStep
    val baseSc = baseScale - initialShrink - (depth - 1) * scaleStep
    val baseOp = (0.85 - (depth - 1) * 0.2).coerceAtLeast(0.45)

    // "Promoted" target = the resting position of depth-1. For depth=1
    // that's the active slot (0, 0, baseScale, 1.0). Peeks interpolate
    // toward this as the user drags so the deck visually advances.
    val promotedTx = if (depth == 1) 0.0 else initialTx + (depth - 2) * txStep
    val promotedTy = if (depth == 1) 0.0 else initialTy + (depth - 2) * tyStep
    val promotedSc = if (depth == 1) baseScale
        else baseScale - initialShrink - (depth - 2) * scaleStep
    val promotedOp = if (depth == 1) 1.0
        else (0.85 - (depth - 2) * 0.2).coerceAtLeast(0.45)

    val t = progress * progress * progress
    return PeekTransform(
        tx = baseTx + (promotedTx - baseTx) * t,
        ty = baseTy + (promotedTy - baseTy) * t,
        scale = baseSc + (promotedSc - baseSc) * t,
        opacity = baseOp + (promotedOp - baseOp) * t,
    )
}
